import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { resolveSandboxMediaUrl, type SandboxCourse } from '../../utils/sandboxCourseApi';
import { updateSandboxCourse, uploadSandboxCoverImage } from '../../redux/sandbox/actions';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { SandboxCoverImagePicker } from './SandboxCoverImagePicker';
import { RichTextEditor } from '../Common/RichTextEditor';

const FOCUS_TAGS = ['Cinematic UGC', 'Lighting & Sound', 'Monetization', 'Viral Hooks', 'AI Workflows'];

type Props = {
    open: boolean;
    course: SandboxCourse | null;
    onClose: () => void;
    onSaved: () => void;
};

export function SandboxEditCourseModal({ open, course, onClose, onSaved }: Props) {
    const dispatchPromise = useDispatchPromise();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [categories, setCategories] = useState<string[]>([]);
    const [coverPreview, setCoverPreview] = useState('');
    const [coverFile, setCoverFile] = useState<File | null>(null);
    const [customTagOpen, setCustomTagOpen] = useState(false);
    const [customTagDraft, setCustomTagDraft] = useState('');
    const [officeHours, setOfficeHours] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !course) return;
        setTitle(course.title || '');
        setDescription(course.description || '');
        setCategories(course.categories?.length ? [...course.categories] : []);
        setCoverPreview(resolveSandboxMediaUrl(course.cover_image) || '');
        setCoverFile(null);
        setCustomTagOpen(false);
        setCustomTagDraft('');
        setOfficeHours(Boolean(course.office_hours_enabled));
    }, [open, course]);

    const customTags = categories.filter(t => !FOCUS_TAGS.includes(t));

    const fieldSx = useMemo(
        () => ({
            '& .MuiOutlinedInput-root': {
                bgcolor: '#323232',
                color: OBSIDIAN.onSurface,
                fontFamily: OBSIDIAN_BODY_FONT,
                fontSize: 14,
                borderRadius: 2,
                '& fieldset': { borderColor: `${OBSIDIAN.outline}55` },
                '&:hover fieldset': { borderColor: OBSIDIAN.outline },
                '&.Mui-focused fieldset': { borderColor: OBSIDIAN.primary },
            },
            '& .MuiInputBase-input': {
                color: OBSIDIAN.onSurface,
                '&::placeholder': { color: OBSIDIAN.onSurfaceVariant, opacity: 1 },
            },
        }),
        [],
    );

    const toggleTag = (tag: string) => {
        setCategories(prev => (prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]));
    };

    const addCustomTag = () => {
        const tag = customTagDraft.trim().slice(0, 40);
        if (!tag) return;
        setCategories(prev => (prev.includes(tag) ? prev : [...prev, tag]));
        setCustomTagDraft('');
        setCustomTagOpen(false);
    };

    const submit = async () => {
        if (!course?._id) return;
        if (!title.trim()) {
            showToastMessage('Course title is required', 'error');
            return;
        }
        if (!categories.length) {
            showToastMessage('Pick at least one focus tag', 'error');
            return;
        }
        setSaving(true);
        try {
            let cover_image = course.cover_image || null;
            if (coverFile) {
                cover_image = (await dispatchPromise(
                    uploadSandboxCoverImage({ file: coverFile, courseId: course._id }),
                )) as string;
            } else if (!coverPreview) {
                cover_image = null;
            }
            await dispatchPromise(
                updateSandboxCourse({
                    courseId: course._id,
                    body: {
                        title: title.trim().slice(0, 75),
                        description: description.trim(),
                        categories,
                        cover_image,
                        office_hours_enabled: officeHours,
                    },
                }),
            );
            showToastMessage('Course updated', 'success');
            onSaved();
            onClose();
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not update course', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            scroll="paper"
            PaperProps={{
                sx: {
                    bgcolor: OBSIDIAN.surfaceContainerLow,
                    backgroundImage: 'none',
                    borderRadius: 3,
                    maxHeight: 'calc(100dvh - 32px)',
                },
            }}
            slotProps={{
                backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' } },
            }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 800,
                        fontSize: 18,
                        color: OBSIDIAN.onSurface,
                    }}>
                    Edit Course
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ bgcolor: OBSIDIAN.surfaceContainerHighest, color: OBSIDIAN.onSurfaceVariant }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2.5,
                    borderColor: OBSIDIAN.surfaceContainerHighest,
                    maxHeight: { xs: 'calc(100dvh - 200px)', sm: 'calc(100dvh - 220px)' },
                    overflowY: 'auto',
                }}>
                <Box>
                    <Typography
                        sx={{
                            mb: 1,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.primary,
                        }}>
                        Course Title *
                    </Typography>
                    <TextField
                        fullWidth
                        value={title}
                        onChange={e => setTitle(e.target.value.slice(0, 75))}
                        inputProps={{ maxLength: 75 }}
                        sx={fieldSx}
                    />
                </Box>

                <Box>
                    <Typography
                        sx={{
                            mb: 1,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.primary,
                        }}>
                        Overview description
                    </Typography>
                    <RichTextEditor
                        value={description}
                        onChange={setDescription}
                        placeholder="What students will learn…"
                        shellVariant="companyAbout"
                        compact
                    />
                </Box>

                <Box>
                    <Typography
                        sx={{
                            mb: 1,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.primary,
                        }}>
                        Cover image
                    </Typography>
                    <SandboxCoverImagePicker
                        previewUrl={coverPreview}
                        onCropped={(preview, file) => {
                            setCoverPreview(preview);
                            setCoverFile(file);
                        }}
                        onClear={() => {
                            setCoverPreview('');
                            setCoverFile(null);
                        }}
                    />
                </Box>

                <Box>
                    <Typography
                        sx={{
                            mb: 1,
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.onSurfaceVariant,
                        }}>
                        Focus Tags *
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {[...FOCUS_TAGS, ...customTags].map(tag => {
                            const active = categories.includes(tag);
                            return (
                                <Box
                                    key={tag}
                                    component="button"
                                    type="button"
                                    onClick={() => toggleTag(tag)}
                                    sx={{
                                        border: 'none',
                                        cursor: 'pointer',
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: 999,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        bgcolor: active ? OBSIDIAN.primary : OBSIDIAN.surfaceContainerHigh,
                                        color: active ? OBSIDIAN.onPrimaryFixed : OBSIDIAN.onSurfaceVariant,
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        fontSize: 12,
                                        fontWeight: active ? 700 : 500,
                                    }}>
                                    {active && <CheckIcon sx={{ fontSize: 14 }} />}
                                    {tag}
                                </Box>
                            );
                        })}
                        <Box
                            component="button"
                            type="button"
                            onClick={() => setCustomTagOpen(v => !v)}
                            sx={{
                                border: 'none',
                                cursor: 'pointer',
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 999,
                                bgcolor: OBSIDIAN.surfaceContainer,
                                color: OBSIDIAN.onSurfaceVariant,
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontSize: 12,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}>
                            <AddIcon sx={{ fontSize: 14 }} /> Custom
                        </Box>
                    </Box>
                    {customTagOpen && (
                        <Box sx={{ mt: 1.25, display: 'flex', gap: 1 }}>
                            <TextField
                                autoFocus
                                fullWidth
                                size="small"
                                placeholder="Custom focus tag"
                                value={customTagDraft}
                                onChange={e => setCustomTagDraft(e.target.value.slice(0, 40))}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addCustomTag();
                                    }
                                }}
                                sx={fieldSx}
                            />
                            <Button
                                onClick={addCustomTag}
                                disabled={!customTagDraft.trim()}
                                sx={{
                                    borderRadius: 999,
                                    background: `linear-gradient(90deg, ${OBSIDIAN.primary}, #feb700)`,
                                    color: OBSIDIAN.onPrimaryFixed,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    px: 2,
                                }}>
                                Add
                            </Button>
                        </Box>
                    )}
                </Box>

                <Box
                    component="label"
                    sx={{
                        display: 'flex',
                        gap: 1.5,
                        p: 1.75,
                        borderRadius: 3,
                        bgcolor: OBSIDIAN.surfaceContainer,
                        cursor: 'pointer',
                    }}>
                    <Checkbox
                        checked={officeHours}
                        onChange={e => setOfficeHours(e.target.checked)}
                        sx={{ color: OBSIDIAN.outline, '&.Mui-checked': { color: OBSIDIAN.primary }, p: 0.5 }}
                    />
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 700,
                                fontSize: 12,
                                color: OBSIDIAN.onSurface,
                            }}>
                            Live Sandbox Office Hours
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontSize: 11,
                                color: OBSIDIAN.onSurfaceVariant,
                            }}>
                            Marks this course as offering office hours. Also set weekly slots + Google Calendar in
                            Studio → Office hours.
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1.5, borderTop: `1px solid ${OBSIDIAN.surfaceContainerHighest}` }}>
                <Button
                    onClick={onClose}
                    sx={{ color: OBSIDIAN.onSurfaceVariant, textTransform: 'none', fontWeight: 600 }}>
                    Cancel
                </Button>
                <Button
                    disabled={saving}
                    onClick={submit}
                    sx={{
                        flex: 1,
                        py: 1.25,
                        borderRadius: 999,
                        background: `linear-gradient(90deg, ${OBSIDIAN.primary}, #feb700)`,
                        color: OBSIDIAN.onPrimaryFixed,
                        textTransform: 'none',
                        fontWeight: 800,
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                    }}>
                    {saving ? 'Saving…' : 'Save changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
